import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';

// Lista di prompt per la scrittura creativa (ispirati a Città, Natura ed Eventi, fortemente descrittivi)
const PROMPTS = [
    // --- CITTÀ ---
    "Ti trovi nel mezzo del caos vibrante di Bangkok, il calore umido della sera ti avvolge. L’aria è satura del fumo pungente dello street food e le luci al neon intermittenti tingono i marciapiedi di rosso e blu. In questo disordine perfetto, quale piccolo, inaspettato dettaglio cattura improvvisamente la tua attenzione e rallenta il tuo respiro?",
    "È l'alba fredda a New York, la città di cemento e vetro sta appena svegliandosi dalla nebbia mattutina. Sei sul sedile posteriore di un taxi giallo e sdrucito, diretto verso un luogo a te ancora ignoto. Osservando il panorama sfuocato fuori dal finestrino e ascoltando il tergicristallo battere il tempo, cosa provi e verso dove immagini di essere diretto?",
    "Passeggi in una rigida serata autunnale tra i grattacieli lucidi e geometrici di Milano, stringendoti nel cappotto. C'è un silenzio quasi surreale, rotto solo dal suono secco dei tuoi passi sul selciato umido. A quale ricordo stai pensando mentre osservi in lontananza la sagoma illuminata del Duomo, che ti fa sentire stranamente protetto?",
    "Piove a dirotto sulle grigie strade di Londra, grosse gocce rimbombano sui tetti. Tu sei al riparo, sotto il tendone rosso e logoro di un vecchio pub, fumando una sigaretta o sorseggiando un tè caldo. Osservi i passanti affrettati nascosti sotto cupi ombrelli neri. All'improvviso passa l'unica persona che cammina lentamente senza ombrello, con il viso inzuppato. Che storia ti inventi sul suo conto?",
    "Ti sei perso nel labirinto di vicoli tortuosi di una vecchia città mediterranea a strapiombo sul mare. I muri irregolari sono dipinti di una calce accecante e il sole scotta sulla pelle. È il primo pomeriggio e tutto tace. Poi, da una finestra o da dietro un portone socchiuso, arriva un suono familiare o un profumo pungente che ti fa sentire che sei esattamente nel posto giusto. Di cosa si tratta?",
    
    // --- NATURA ---
    "Sei immerso nel cuore più profondo ed antico del bosco umido, dove l'odore pungente di muschio e aghi di pino riempie i polmoni. La luce verdastra filtra a malapena tra le fronde intricate. Il silenzio è interrotto solo da un lieve scricchiolio e da un fruscio alle tue spalle. Sai per certo che non è un animale. Cos'è, e cosa vuole dirti quell'entità misteriosa?",
    "Dopo ore infinite di faticosa e dolorosa camminata sul sentiero ghiacciato, sei finalmente arrivato in vetta alla montagna più alta. L'aria è sottile, tagliente e ti mozza il respiro, ma sotto di te il panorama di valli innevate è sconfinato, luminoso e abbacinante. Quale pesante fardello emotivo, o insicurezza profonda, senti di aver fisicamente lasciato ad appassire giù nella valle?",
    "Sei comodamente sdraiato supino su un prato sterminato e collinare, nel primo caldo della primavera. I fili d'erba alta e selvatica ti accarezzano il viso e ti nascondono al mondo, mentre i grilli suonano. Guardi in alto, verso le nuvole candide e spesse che galoppano veloci nel cielo turchino. Fino a quando una nuvola gigantesca prende una forma particolare, proprio quella che ruba tutta la tua attenzione. Che forma è e perché ti turba o ti rasserena?",
    "Sei seduto a gambe incrociate su uno scoglio di pietra nera e ruvida di fronte a un oceano burrascoso. Le nuvole plumbee si fondono col mare e onde torreggianti, spumose, si infrangono potentemente lanciando schizzi freddi sul tuo volto intorpidito. Quella forza bruta, indomabile e spaventosa del maremoto... quale istinto vitale assopito risveglia dentro di te?",
    "È notte fonda, freddissima, e ti sei allontanano dal gruppo nel bel mezzo del deserto rosso. C'è solo una sabbia finissima sotto ai piedi e sei avvolto dal silenzio spettrale, il più assoluto che tu abbia mai udito, sotto la volta di milioni di stelle puntiformi e gigantesche. Sentendoti così disarmantemente piccolo, ma parte del tutto, che promessa solenne fai a te stesso qui, nel nulla?",

    // --- EVENTI / LUOGHI ---
    "Sei esattamente in mezzo alla folla compatta e urlante di un concerto straripante, ad ascoltare il brano più intenso della tua vita. Le luci stroboscopiche azzurre pulsano follemente in perfetta sincronia con la batteria e il terreno trema sotto la cassa armonica. In mezzo alla massa di diecimila sconosciuti sudati che saltano, improvvisamente tutto sembra ovattarsi. Quale dettaglio, del tuo corpo o di ciò che vedi, ti fa ritrovare il tuo baricentro profondo?",
    "È sera fonda in un ristorante dallo stile liberty, dalle tovaglie di lino bianco e luci soffuse con candelabri d'argento. Sorseggi del vino rosso, da solo al tavolo, attendendo una persona che forse non si presenterà mai. Al di là del profumo d'arrosto e del tintinnio dei calici, il tuo sguardo viene rapito dalla coppia al tavolo accanto, che sembra litigare o innamorarsi per l'ultima volta. Cosa ti incuriosisce ossessivamente del loro misterioso modo di comunicare tra le righe?",
    "È il crepuscolo di una domenica pomeriggio stanca e malinconica in casa tua: sei accoccolato sul divano più comodo, una tisana fumante appoggiata e mezza vuota sul tavolino, il silenzio rotto solo dal ronzio del frigo. È l'unico tuo vero spazio inviolabile nel mondo. Fissa un piccolo, banalissimo e insignificante dettaglio o oggetto sbeccato in un angolo di quest'unica stanza che, più di ogni altra cosa, stasera ti urla rassicurante: 'se in salvo, sei a casa'.",
    "Ti ritrovi intrappolato in una lentissima coda alla cassa di un vecchio supermercato di periferia, stordito dalla luce bianca ospedaliera. La vecchietta minuta che paga in monete proprio davanti a te, ignara dei tuoi sbuffi, ha messo sul nastro trasportatore tre oggetti unici e completamente slegati tra loro: un cappello sgangherato da prestigiatore, uno spago giallo neon, un solo tubetto di vernice rosso sangue. Prova a viaggiare con la mente per tentare di ricostruire passo dopo passo la sua giornata eccezionale ed eroica fino a questo istante.",
    "Sei in una stazione ferroviaria maestosa, di primo mattino. Le luci basse penetrano i soffitti ad arco in ghisa, e tutti gli enormi binari sono invasi da scoppiettanti sbuffi di vapore caldissimo e fuliggine scura: sei chiaramente in un'epoca passata. Sui binari di legno decine di valigie di cuoio e fischi di macchinisti stordiscono le orecchie, e l'intero scalo bolle di anime e abbracci d'addio in partenza. Dagli altoparlanti gracchianti senti l'annuncio rimbombante incomprensibile di un treno, pronto agli sbuffi, per una destinazione nebbiosa e remota: cosa nello stomaco ti trattiene radicato, o ti spinge irresistibilmente a rincorrere il penultimo predellino scuro all'ultimo istante con la tua valigia?"
];

const CreativeWritingTool = ({ client }) => {
    const [prompt, setPrompt] = useState('');
    const [usePrompt, setUsePrompt] = useState(true);
    const [text, setText] = useState('');
    const [title, setTitle] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null); // null, 'success', 'error'

    // Assicura che un nuovo prompt venga caricato al montaggio del componente
    useEffect(() => {
        generateNewPrompt();
    }, []);

    const generateNewPrompt = () => {
        const randomIndex = Math.floor(Math.random() * PROMPTS.length);
        setPrompt(PROMPTS[randomIndex]);
    };

    const handleSave = async () => {
        if (!text.trim()) {
            alert('Scrivi qualcosa prima di salvare.');
            return;
        }

        setIsSaving(true);
        setSaveStatus(null);

        try {
            // Se l'utente non ha impostato un titolo, ne generiamo uno in base alla modalità
            const finalTitle = title.trim() 
                ? title.trim() 
                : (usePrompt ? `Scrittura Creativa: Isprato da uno spunto` : `Scrittura Libera`);

            // Combiniamo il prompt e il testo se l'utente ha usato lo spunto
            const combinedText = usePrompt 
                ? `**Spunto ricevuto:** ${prompt}\n\n${text}`
                : text;

            const contentPayload = {
                title: finalTitle,
                text: combinedText,
                category: 'Diario',
                userTags: ['scrittura-creativa']
            };

            await DataManager.addWork(client.id, {
                type: 'journal',
                content: contentPayload,
                timestamp: new Date().toISOString(),
                shared: true // Di default lo rendiamo condiviso col counselor
            });

            setSaveStatus('success');
            setText('');
            setTitle('');
            
            // Ripristina lo stato dopo 3 secondi
            setTimeout(() => setSaveStatus(null), 3000);
        } catch (error) {
            console.error('Errore durante il salvataggio:', error);
            setSaveStatus('error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-orange-50/30 rounded-3xl p-6 border border-orange-100">
            {/* Header e Selezione Modalità */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                    <h3 className="text-xl font-black text-orange-950 flex items-center gap-2">
                        <span>✍️</span> Flusso Creativo
                    </h3>
                    <p className="text-xs text-orange-700/70 font-bold uppercase tracking-widest mt-1">
                        Esplora i tuoi pensieri attraverso la scrittura
                    </p>
                </div>

                <div className="bg-white rounded-xl p-1 shadow-sm border border-orange-100 flex gap-1">
                    <button
                        onClick={() => setUsePrompt(true)}
                        className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                            usePrompt 
                                ? 'bg-orange-100 text-orange-800 shadow-sm' 
                                : 'text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                        💡 Usa Spunto
                    </button>
                    <button
                        onClick={() => setUsePrompt(false)}
                        className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                            !usePrompt 
                                ? 'bg-orange-100 text-orange-800 shadow-sm' 
                                : 'text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                        📄 Scrittura Libera
                    </button>
                </div>
            </div>

            {/* Area Prompt / Ispirazione */}
            {usePrompt && (
                <div className="mb-6 bg-gradient-to-r from-orange-100/50 to-amber-50/50 p-6 rounded-2xl border border-orange-200/50 relative group">
                    <div className="absolute top-2 right-2 opacity-10 text-4xl pointer-events-none">✨</div>
                    <p className="text-orange-900 font-medium italic text-sm relative z-10 leading-relaxed">
                        "{prompt}"
                    </p>
                    <button 
                        onClick={generateNewPrompt}
                        className="mt-3 text-[10px] font-bold text-orange-600 uppercase tracking-wider hover:text-orange-800 transition flex items-center gap-1"
                    >
                        <span>🔄</span> Cambia Spunto
                    </button>
                </div>
            )}

            {!usePrompt && (
                <div className="mb-6 bg-white p-4 rounded-2xl border border-gray-100">
                    <p className="text-gray-500 font-medium italic text-sm text-center">
                        Spazio bianco. Lascia fluire liberamente le parole senza un tema predefinito.
                    </p>
                </div>
            )}

            {/* Area Editor */}
            <div className="flex-1 flex flex-col gap-4">
                <input
                    type="text"
                    placeholder="Dai un titolo alla tua scrittura (opzionale)..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full bg-white border border-orange-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 transition shadow-sm font-medium text-gray-800"
                />

                <textarea
                    placeholder="Inizia a scrivere qui..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="w-full flex-1 min-h-[250px] bg-white border border-orange-100 rounded-2xl p-5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-orange-300 transition shadow-sm resize-none text-gray-800 leading-relaxed"
                />
            </div>

            {/* Area Pulsanti e Stato */}
            <div className="mt-6 flex items-center justify-between border-t border-orange-200/50 pt-4">
                <div className="text-xs font-medium">
                    {saveStatus === 'success' && (
                        <span className="text-green-600 flex items-center gap-1 animate-fade-in">
                            <span>✅</span> Salvato nel tuo Diario!
                        </span>
                    )}
                    {saveStatus === 'error' && (
                        <span className="text-red-600 flex items-center gap-1 animate-fade-in">
                            <span>❌</span> Errore nel salvataggio
                        </span>
                    )}
                    {!saveStatus && text.length > 0 && (
                        <span className="text-orange-500">
                            {text.trim().split(/\s+/).length} parole
                        </span>
                    )}
                </div>

                <button
                    onClick={handleSave}
                    disabled={isSaving || !text.trim()}
                    className={`px-8 py-3 rounded-xl text-sm font-bold uppercase tracking-widest transition-all focus:outline-none shadow-md flex items-center gap-2 ${
                        isSaving || !text.trim()
                            ? 'bg-orange-200 text-orange-400 cursor-not-allowed shadow-none'
                            : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 hover:shadow-lg hover:shadow-orange-200'
                    }`}
                >
                    {isSaving ? '⏳ Salvataggio...' : '💾 Salva nel Diario'}
                </button>
            </div>
        </div>
    );
};

export default CreativeWritingTool;
